import { useContext } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { basicDateFormat, timeUntil } from "../../tools/Helpers";
import { ArrowIcon, ArrowRightIcon, HiddenIcon, PublishIcon, PublishPersonalIcon, RecommendIcon } from "../../assets/icons";
import { listSetPeriodSingular } from "../../constants/general";
import { fallbackImage } from "../../constants/urls";
import { Link } from "react-router-dom";
import Modal from "../Modal";
import { useGetPeriodsBySet } from "../../api/data-access";
import { DecoratedCollection, DecoratedList, ListSet } from "../../types/SharedTypes";

export const CurrentPeriodButton = ({setVisible, createdAt}: {setVisible?: (prev: boolean) => void, createdAt: string}) => {
  
  const {authUserId} = useContext(AuthContext);

  if (!setVisible || !authUserId) return (
    <div className="tag prevent-shrink" data-secondary={true} data-small={true} data-themed={true}>
      <p className="subtitle-medium header">{basicDateFormat(createdAt)}</p>
    </div>
  )
  return (
    <button className="empty darkness-hover prevent-shrink" onClick={() => setVisible(true)}>
      <div className="tag prevent-shrink" data-secondary={true} data-small={true} data-themed={true}>
        <p className="subtitle-medium header">{basicDateFormat(createdAt)}</p>
      </div>
    </button>
  )
}

export const AllPeriodsModal = ({listSet, setModalVisible}: {listSet: ListSet, setModalVisible: (prev: boolean) => void}) => {
  
  const {data, isSuccess, isLoading} = useGetPeriodsBySet(listSet._id);
  
  return (
    <Modal
      setModalVisible={setModalVisible}
      modalTitle={`All Periods in Set`}
      controls
      removePadding
      isLoading={isLoading}
      scroll
    >
      <div className="item-list no-gap">
        {isSuccess && data.map(item =>
          <button key={item._id} className="empty item-list">
            <Link
              to={`/${item.type}/${item._id}?theme=${item.theme}`}
              className="bottom-border secondary-border padding item-row large full-width"
              onClick={() => setModalVisible(false)}
            >
              <div className="thumb small square">
                <img src={item.image || fallbackImage} />
              </div>
              <div className="item-list med">
                <div className="item-row large">
                  <p className="body ellipsis">{`${listSetPeriodSingular(listSet.period, true)} of ${basicDateFormat(item.createdAt)}`}</p>
                  {item.isDraft && 
                    <div className="icon xsmall" title={"Draft"}>
                      <HiddenIcon />
                    </div>
                  }
                  <div className="item-row">
                    {!item.isDraft &&
                      <div className="icon xsmall" title={item.isPublic ? "Published" : "Published Personal"}>
                        {item.isPublic ? <PublishIcon /> : <PublishPersonalIcon /> }
                      </div>
                    }
                    {!item.originalListPost && 
                      <div className="icon xsmall" title={"Original"}>
                        <RecommendIcon />
                      </div>
                    }
                  </div>
                </div>
                <p className="body secondary ellipsis">{`${item.entriesCount} Published Lists`}</p>
              </div>
            </Link>
          </button>
        )}
      </div>
    </Modal>
  )
}

type Props = {
  listPost: DecoratedCollection | DecoratedList;
  type: 'collection' | 'list';
  setAllPeriodsVisible: (prev: boolean) => void;
  removePadding?: boolean;
}

const SeriesTimeline = ({listPost, setAllPeriodsVisible, type, removePadding}: Props) => {
  if (!listPost.listSet) return (
    <div className="item-list center-horizontal padding-xs">
      <CurrentPeriodButton createdAt={listPost.createdAt} />
    </div>
  )
  return (
    <div className={`item-list ${removePadding ? '' : "padding-small"}`}>
      {listPost.listSet && listPost.listSet.renewAt && !listPost.nextPeriod && listPost.isAuth &&
        <p className="subtitle-small secondary center weight-light">
          {listPost.listSet.isDisabled 
            ? "Period creation disabled"
            : `Next period in ${timeUntil(listPost.listSet?.renewAt)}`
          }
        </p>
      }
      <div className={`item-row large full-width center-horizontal ${removePadding ? '' : "padding-horizontal"}`}>
        <Link 
          aria-disabled={!listPost.prevPeriod}
          to={`/${type}/${listPost.prevPeriod || listPost._id}?theme=${listPost.theme}`}
          replace
        >
          <button className="icon small">
            <ArrowIcon />
          </button>
        </Link>
        <div className="full-width bottom-border dotted" />
        <CurrentPeriodButton setVisible={setAllPeriodsVisible} createdAt={listPost.createdAt} />
        <div className="full-width bottom-border dotted" />
        <Link 
          aria-disabled={!listPost.nextPeriod}
          to={`/${type}/${listPost.nextPeriod || listPost._id}?theme=${listPost.theme}`}
          replace
        >
          <button className="icon small">
            <ArrowRightIcon />
          </button>
        </Link>
      </div>
    </div>  
  )
}
export default SeriesTimeline;